import * as React from "react";

import { AuthContext } from "@/auth/AuthContext";
import { accountsApi } from "@/lib/accounts-api";
import { CustomerApi } from "@/lib/customer-api";
import { getApiErrorMessage } from "@/lib/error-codes";
import { onUnauthorized } from "@/lib/http-client";
import i18n from "@/lib/i18n";
import { exchangeOtp, isMaskedCustomerToken, peekOtpCodeFromHash } from "@/lib/mask-session";
import type { AuthState, AuthUser, LoginPayload, RegisterPayload } from "@/types";
import type { Customer } from "@/types/customer";

function customerStub(customerId: number): Customer {
  return {
    customerId,
    email: "",
    phone: "",
    name: "",
    createdAt: "",
    updatedAt: null,
    marketing: false,
    blocked: false,
    blockedBy: null,
    blockedReason: null,
    blockedAt: null,
    customerType: "person",
  };
}

const sessionExpired = () => i18n.t("errors.system.unauthorized");

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [pendingOtpCode] = React.useState<string | null>(() => peekOtpCodeFromHash());
  const otpExchangeStarted = React.useRef(false);

  const [state, setState] = React.useState<AuthState>(() => {
    if (pendingOtpCode) {
      return {
        isLoading: true,
        isRestoring: true,
        isAuthenticated: false,
        token: null,
        user: null,
        customers: [],
        selectedCustomer: null,
        error: null,
      };
    }

    const hasCustomerSession = accountsApi.hasActiveCustomerSession();
    const customerToken = accountsApi.getCustomerToken();
    const accountToken = accountsApi.getAccountToken();
    const selectedCustomerId = accountsApi.getSelectedCustomerId();

    if (hasCustomerSession && customerToken && selectedCustomerId != null) {
      return {
        isLoading: false,
        isRestoring: false,
        isAuthenticated: true,
        token: customerToken,
        user: null,
        customers: [],
        selectedCustomer: customerStub(selectedCustomerId),
        error: null,
      };
    }

    return {
      isLoading: !!accountToken,
      isRestoring: !!accountToken,
      isAuthenticated: false,
      token: null,
      user: null,
      customers: [],
      selectedCustomer: null,
      error: null,
    };
  });

  const applyAuthenticatedState = React.useCallback(
    (token: string, user: AuthUser | null, customers: Customer[], selectedCustomerId?: number | null) => {
      const selectedCustomer =
        selectedCustomerId == null
          ? customers.length === 1
            ? customers[0]
            : null
          : customers.find((item) => item.customerId === selectedCustomerId) ??
            customerStub(selectedCustomerId);

      setState({
        isLoading: false,
        isRestoring: false,
        isAuthenticated: true,
        token,
        user,
        customers,
        selectedCustomer,
        error: null,
      });
    },
    []
  );

  const clearSession = React.useCallback((error: string | null = null) => {
    accountsApi.clearAuth();
    setState({
      isLoading: false,
      isRestoring: false,
      isAuthenticated: false,
      token: null,
      user: null,
      customers: [],
      selectedCustomer: null,
      error,
    });
  }, []);

  React.useEffect(() => onUnauthorized(() => clearSession(sessionExpired())), [clearSession]);

  React.useEffect(() => {
    async function loadSession() {
      if (pendingOtpCode) {
        if (otpExchangeStarted.current) {
          // React StrictMode re-invokes effects; the exchange already
          // started (and the code is single-use), so this second pass has
          // nothing to do — the first pass's continuation applies the state.
          return;
        }
        otpExchangeStarted.current = true;

        try {
          const result = await exchangeOtp(pendingOtpCode);
          if (!result) {
            clearSession("Ссылка для входа устарела или уже использована.");
            return;
          }
        } catch {
          clearSession("Ссылка для входа устарела или уже использована.");
          return;
        }
      }

      const customerToken = accountsApi.getCustomerToken();
      const accountToken = accountsApi.getAccountToken();

      if (!customerToken && !accountToken) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          isRestoring: false,
        }));
        return;
      }

      const selectedCustomerId = accountsApi.getSelectedCustomerId();
      // A session that ended (a 401 elsewhere) while this check was out is not brought back by its answer.
      const ended = () =>
        accountsApi.getCustomerToken() !== customerToken || accountsApi.getAccountToken() !== accountToken;

      if (selectedCustomerId != null && customerToken) {
        if (isMaskedCustomerToken(customerToken)) {
          try {
            const customer = await CustomerApi.getMe();
            if (ended()) return;
            applyAuthenticatedState(customerToken, null, [customer], selectedCustomerId);
          } catch {
            clearSession(sessionExpired());
          }
          return;
        }

        try {
          const { user, customers } = await accountsApi.getAccountContext();
          if (ended()) return;
          const customerExists = customers.some((item) => item.customerId === selectedCustomerId);

          if (!customerExists) {
            accountsApi.clearSelectedCustomer();
            const fallbackToken = accountsApi.getAccountToken();
            if (!fallbackToken) {
              clearSession(sessionExpired());
              return;
            }

            // The one customer left is chosen as at sign-in, with a session of its own.
            if (customers.length === 1) {
              const newCustomerToken = await accountsApi.activateCustomerSession(customers[0].customerId);
              applyAuthenticatedState(newCustomerToken, user, customers, customers[0].customerId);
              return;
            }

            applyAuthenticatedState(fallbackToken, user, customers, null);
            return;
          }

          applyAuthenticatedState(customerToken, user, customers, selectedCustomerId);
        } catch {
          clearSession(sessionExpired());
        }
        return;
      }

      if (!accountToken) {
        clearSession();
        return;
      }

      try {
        const { user, customers } = await accountsApi.getAccountContext();
        if (ended()) return;
        if (customers.length === 1) {
          const newCustomerToken = await accountsApi.activateCustomerSession(customers[0].customerId);
          applyAuthenticatedState(newCustomerToken, user, customers, customers[0].customerId);
          return;
        }

        applyAuthenticatedState(accountToken, user, customers, null);
      } catch {
        clearSession(sessionExpired());
      }
    }

    void loadSession();
  }, [pendingOtpCode, applyAuthenticatedState, clearSession]);

  const register = React.useCallback(
    async (payload: RegisterPayload) => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      try {
        await accountsApi.register(payload);
        const session = await accountsApi.login({
          email: payload.email,
          password: payload.password,
        });
        const customers = await accountsApi.customers();
        if (customers.length === 1) {
          const customerToken = await accountsApi.activateCustomerSession(customers[0].customerId);
          applyAuthenticatedState(customerToken, session.user, customers, customers[0].customerId);
          return;
        }

        accountsApi.clearSelectedCustomer();
        applyAuthenticatedState(session.token, session.user, customers, null);
      } catch (error) {
        // An account token taken before the failure would sign the customer in on the next visit.
        accountsApi.clearAuth();
        setState({
          isLoading: false,
          isRestoring: false,
          isAuthenticated: false,
          token: null,
          user: null,
          customers: [],
          selectedCustomer: null,
          // The caller shows why the sign-in failed; `error` is why a session ended.
          error: null,
        });
        throw error;
      }
    },
    [applyAuthenticatedState],
  );

  const login = React.useCallback(
    async (payload: LoginPayload) => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      try {
        const session = await accountsApi.login(payload);
        const customers = await accountsApi.customers();
        if (customers.length === 1) {
          const customerToken = await accountsApi.activateCustomerSession(customers[0].customerId);
          applyAuthenticatedState(customerToken, session.user, customers, customers[0].customerId);
          return;
        }

        accountsApi.clearSelectedCustomer();
        applyAuthenticatedState(session.token, session.user, customers, null);
      } catch (error) {
        // An account token taken before the failure would sign the customer in on the next visit.
        accountsApi.clearAuth();
        setState({
          isLoading: false,
          isRestoring: false,
          isAuthenticated: false,
          token: null,
          user: null,
          customers: [],
          selectedCustomer: null,
          // The caller shows why the sign-in failed; `error` is why a session ended.
          error: null,
        });
        throw error;
      }
    },
    [applyAuthenticatedState]
  );

  const logout = React.useCallback(async () => {
    await accountsApi.logout();
    setState({
      isLoading: false,
      isRestoring: false,
      isAuthenticated: false,
      token: null,
      user: null,
      customers: [],
      selectedCustomer: null,
      error: null,
    });
  }, []);

  const selectCustomer = React.useCallback(async (customerId: number) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const customerToken = await accountsApi.activateCustomerSession(customerId);
      const { user, customers } = await accountsApi.getAccountContext();
      applyAuthenticatedState(customerToken, user, customers, customerId);
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: getApiErrorMessage(error),
      }));
      throw error;
    }
  }, [applyAuthenticatedState]);

  const switchCustomer = React.useCallback(async () => {
    await accountsApi.deactivateCustomerSession();

    const accountToken = accountsApi.getAccountToken();
    if (!accountToken) {
      clearSession();
      return;
    }

    setState((prev) => ({
      ...prev,
      isLoading: false,
      isAuthenticated: true,
      token: accountToken,
      selectedCustomer: null,
      error: null,
    }));
  }, [clearSession]);

  const value = React.useMemo(
    () => ({
      ...state,
      isMasked: isMaskedCustomerToken(state.token),
      login,
      register,
      logout,
      selectCustomer,
      switchCustomer,
    }),
    [state, login, register, logout, selectCustomer, switchCustomer]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
